import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, defer, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import * as crypto from 'crypto-js';

import { environment } from 'src/environments/environment';
import { StorageKey } from '../models/common';
import { StorageService } from './storage.service';
import { ApiErrorMessages } from '../utils/api-error.utils';

export interface RefreshedTokens {
  token: string;
  refreshToken: string;
}

@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private refreshTokenError: Error | null = null;

  constructor(
    private storageSvc: StorageService,
    private http: HttpClient
  ) {}

  refreshAccessToken(): Observable<RefreshedTokens> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenError = null;
      this.refreshTokenSubject.next(null);

      return this.requestTokenRefresh().pipe(
        switchMap((res) => {
          this.isRefreshing = false;
          this.refreshTokenError = null;
          const newToken = res?.token;
          const newRefreshToken = res?.refreshToken;

          if (!newToken) {
            const error = new Error(ApiErrorMessages.sessionExpired);
            this.refreshTokenError = error;
            this.refreshTokenSubject.next(null);
            return throwError(() => error);
          }

          return forkJoin([
            this.storageSvc.set(StorageKey.jwt, newToken, true),
            this.storageSvc.set(StorageKey.refreshToken, newRefreshToken, true),
          ]).pipe(
            switchMap(() => {
              this.refreshTokenSubject.next(newToken);
              return of({ token: newToken, refreshToken: newRefreshToken });
            })
          );
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshTokenError = err;
          this.refreshTokenSubject.next(null);
          return throwError(() => err);
        })
      );
    }

    if (this.refreshTokenError) {
      return throwError(() => this.refreshTokenError);
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token != null || this.refreshTokenError != null),
      take(1),
      timeout(30000),
      switchMap((token) => {
        if (this.refreshTokenError) {
          return throwError(() => this.refreshTokenError);
        }

        if (!token) {
          return throwError(() => new Error('Token refresh failed'));
        }

        const refreshToken = this.storageSvc.get(StorageKey.refreshToken);
        return of({ token, refreshToken });
      }),
      catchError((err) => throwError(() => err))
    );
  }

  private requestTokenRefresh(): Observable<{ token?: string; refreshToken?: string }> {
    const refreshToken = this.storageSvc.get(StorageKey.refreshToken);
    if (!refreshToken) {
      return throwError(() => new Error(ApiErrorMessages.noRefreshToken));
    }

    const url = `${environment.apiUrl}/v1/User/refresh/jwt?refreshToken=${encodeURIComponent(refreshToken)}`;
    const apiKeyStr = `${environment.encryptionKey}#${this.getCurrentDateTimeInISOFormat()}`;
    const key = crypto.enc.Base64.parse(environment.encryptionKey);
    const encrypted = crypto.AES.encrypt(apiKeyStr, key, {
      mode: crypto.mode.ECB,
      padding: crypto.pad.Pkcs7,
    });
    const base64CipherText = encrypted.ciphertext.toString(crypto.enc.Base64);

    const headers = new HttpHeaders({
      ApiKey: base64CipherText,
    });

    return defer(() => this.http.post<{ token?: string; refreshToken?: string }>(url, null, { headers }));
  }

  private getCurrentDateTimeInISOFormat(): string {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');
    const seconds = String(now.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  }
}
